import React, { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import Loader from '../../../shared/components/ui/Loader';
import {
  EmptyState,
  SectionCard,
  Table,
  TableCard,
  TBody,
  Td,
  Th,
  THead
} from '../../../shared/components/ui/primitives';
import {
  getSiteWalletReimbursements,
  markSiteWalletReimbursed,
  markSiteWalletSettlementPaid
} from '../../services/api/implementation/impl';
import { useRefreshWallets } from './WalletActions';
import {
  BillLinks,
  HolderName,
  PaidModal,
  SmallButton,
  errorMessage,
  money,
  shortDate
} from './siteWalletUi';

/**
 * Own-pocket money Accounts owes back, grouped by site engineer, and the
 * payouts left over from settled wallets. One payment can cover several
 * expenses of the same person: they all get the same mode and reference.
 */
const ReimbursementsPanel = ({ role }) => {
  const refresh = useRefreshWallets();
  const [selected, setSelected] = useState({});
  const [paying, setPaying] = useState(null);

  const data = useQuery({
    queryKey: ['siteWalletReimbursements'],
    queryFn: () => getSiteWalletReimbursements()
  });
  const groups = data.data?.by_holder ?? [];
  const payouts = data.data?.settlement_payouts ?? [];

  // One request per expense; the server has no bulk call. Stops at the first
  // refusal so nothing after it is marked paid by mistake.
  const pay = useMutation({
    mutationFn: async ({ kind, ids, payload }) => {
      for (const id of ids) {
        if (kind === 'payout') await markSiteWalletSettlementPaid(id, payload);
        else await markSiteWalletReimbursed(id, payload);
      }
      return ids.length;
    },
    onSuccess: (n, vars) => {
      refresh();
      setPaying(null);
      setSelected({});
      toast.success(
        vars.kind === 'payout' ? 'Payout recorded' : `${n} reimbursement${n === 1 ? '' : 's'} paid`
      );
    },
    onError: (err) => {
      refresh();
      toast.error(errorMessage(err, 'Could not record the payment'));
    }
  });

  if (data.isLoading) return <Loader />;
  if (!groups.length && !payouts.length) {
    return <EmptyState title="Nothing to pay back" hint="Approved own-pocket amounts show up here." />;
  }

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));

  return (
    <div className="space-y-4">
      {groups.map((g) => {
        // Nobody marks their own money paid.
        const payable = g.expenses.filter((e) => e.user_id !== role.userId);
        const picked = payable.filter((e) => selected[e.id]);
        const pickedTotal = picked.reduce((s, e) => s + Number(e.own_amount || 0), 0);
        return (
          <TableCard
            key={g.holder?.id ?? g.expenses[0]?.user_id}
            title={
              <span className="flex flex-wrap items-center gap-2">
                <HolderName user={g.holder} />
                <span className="text-gray-500">· {money(g.total)} due</span>
              </span>
            }
            action={
              payable.length > 0 && (
                <SmallButton
                  size="md"
                  tone="primary"
                  disabled={!picked.length}
                  onClick={() =>
                    setPaying({
                      kind: 'expense',
                      ids: picked.map((e) => e.id),
                      title: `Pay ${money(pickedTotal)} to ${g.holder?.name ?? 'site engineer'}`,
                      intro: `${picked.length} expense${picked.length === 1 ? '' : 's'} will be marked reimbursed with this reference.`
                    })
                  }>
                  Pay selected{picked.length ? ` (${money(pickedTotal)})` : ''}
                </SmallButton>
              )
            }>
            <Table>
              <THead>
                <tr>
                  <Th>
                    <input
                      type="checkbox"
                      aria-label="Select all"
                      checked={payable.length > 0 && picked.length === payable.length}
                      onChange={(e) =>
                        setSelected((s) => {
                          const next = { ...s };
                          payable.forEach((x) => {
                            next[x.id] = e.target.checked;
                          });
                          return next;
                        })
                      }
                    />
                  </Th>
                  <Th>Spent on</Th>
                  <Th>Project · Category</Th>
                  <Th align="right">Expense</Th>
                  <Th align="right">Own pocket</Th>
                  <Th>Bill</Th>
                  <Th>Approved</Th>
                </tr>
              </THead>
              <TBody>
                {g.expenses.map((e) => {
                  const own = e.user_id === role.userId;
                  return (
                    <tr key={e.id} className={own ? 'opacity-60' : ''}>
                      <Td>
                        <input
                          type="checkbox"
                          aria-label="Select"
                          disabled={own}
                          checked={Boolean(selected[e.id])}
                          onChange={() => toggle(e.id)}
                        />
                      </Td>
                      <Td className="whitespace-nowrap">{shortDate(e.spent_on)}</Td>
                      <Td>
                        <div className="text-gray-900">{e.project_name ?? '—'}</div>
                        <div className="text-sm text-gray-500">{e.category_name ?? '—'}</div>
                      </Td>
                      <Td align="right" numeric className="text-gray-600">
                        {money(e.amount)}
                      </Td>
                      <Td align="right" numeric className="font-semibold">
                        {money(e.own_amount)}
                      </Td>
                      <Td>
                        <BillLinks urls={e.bill_urls} />
                      </Td>
                      <Td className="whitespace-nowrap text-gray-600">{shortDate(e.decided_at)}</Td>
                    </tr>
                  );
                })}
              </TBody>
            </Table>
          </TableCard>
        );
      })}

      {payouts.length > 0 && (
        <SectionCard
          title="Payouts after settlement"
          subtitle="Owed more than they held when the wallet was closed">
          <div className="divide-y divide-gray-100">
            {payouts.map((p) => (
              <div key={p.wallet_id} className="flex flex-wrap items-center justify-between gap-3 py-2">
                <div>
                  <HolderName user={p.holder} />
                  <div className="text-sm text-gray-500">Closed {shortDate(p.closed_at)}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-semibold tabular-nums">{money(p.amount)}</span>
                  {p.holder?.id !== role.userId && (
                    <SmallButton
                      size="md"
                      tone="primary"
                      onClick={() =>
                        setPaying({
                          kind: 'payout',
                          ids: [p.wallet_id],
                          title: `Pay ${money(p.amount)} to ${p.holder?.name ?? 'site engineer'}`
                        })
                      }>
                      Mark paid
                    </SmallButton>
                  )}
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {paying && (
        <PaidModal
          title={paying.title}
          intro={paying.intro}
          busy={pay.isPending}
          onClose={() => setPaying(null)}
          onSubmit={(payload) => pay.mutate({ kind: paying.kind, ids: paying.ids, payload })}
        />
      )}
    </div>
  );
};

export default ReimbursementsPanel;
